import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid,
  CircularProgress,
  TextField,
  Chip,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { getDecryptedAccessToken } from '../../../Utils/LoginUserDetails.js/LoginUserSavedDetails';
import ApiServices from '../../../Utils/ApiServices/ApiServices';

const BenefitsAvailable = ({ onExerciseClick }) => {
  const accessToken = getDecryptedAccessToken();
  const [benefits, setBenefits] = useState([]);
  const [filteredBenefits, setFilteredBenefits] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [benefitToDelete, setBenefitToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchBenefits();
  }, []);

  const fetchBenefits = async () => {
    setLoading(true);
    try {
      const response = await ApiServices.get('/apis/default/api/membershipbenefits', {
        Authorization: `Bearer ${accessToken}`,
      });

      const parsedBenefits = response?.data?.map(benefit => ({
        ...benefit,
        description: JSON.parse(benefit.description),
        faqs: JSON.parse(benefit.faqs),
      }));

      setBenefits(parsedBenefits);
      setFilteredBenefits(parsedBenefits);
    } catch (error) {
      console.error('Failed to fetch membership benefits:', error);
      setSnackbar({ open: true, message: 'Failed to fetch benefits.', severity: 'error' });
      setBenefits([]);
      setFilteredBenefits([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchQuery(value);
    const filtered = benefits.filter((benefit) =>
      benefit.benefit_name.toLowerCase().includes(value)
    );
    setFilteredBenefits(filtered);
  };

  const handleDeleteClick = (benefit) => {
    setBenefitToDelete(benefit);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      await ApiServices.delete(`/apis/default/api/membershipbenefits/${benefitToDelete.id}`, {
        Authorization: `Bearer ${accessToken}`,
      });
      setSnackbar({ open: true, message: 'Benefit deleted successfully.', severity: 'success' });
      setDeleteDialogOpen(false);
      fetchBenefits(); // Refresh list
    } catch (error) {
      console.error('Delete failed:', error);
      setSnackbar({ open: true, message: 'Failed to delete benefit.', severity: 'error' });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h5" mb={2}>Membership Benefits</Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
          <CircularProgress />
        </Box>
      ) : filteredBenefits?.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', mt: 5 }}>
          No benefits available.
        </Typography>
      ) : (
        <>
        
      <TextField
        label="Search Benefits"
        variant="outlined"
        fullWidth
        value={searchQuery}
        onChange={handleSearch}
        sx={{ mb: 3 }}
      />

        <Grid container spacing={3}>
          {filteredBenefits?.map((benefit) => (
            <Grid item xs={12} sm={6} md={4} key={benefit.id}>
              <Card sx={{ height: '100%', position: 'relative' }}>
                <CardMedia
                  component="img"
                  height="180"
                  image={benefit.image}
                  alt={benefit.benefit_name}
                />
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ cursor: 'pointer' }}
                    onClick={() => onExerciseClick(benefit)}
                  >
                    {benefit.benefit_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.subtitle}
                  </Typography>
                  <Box mt={1} display="flex" justifyContent="space-between" alignItems="center">
                    <Chip
                      label={benefit.active === '1' ? 'Active' : 'Inactive'}
                      color={benefit.active === '1' ? 'success' : 'default'}
                      size="small"
                    />
                    <IconButton
                      aria-label="delete"
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(benefit)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete benefit "{benefitToDelete?.benefit_name}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BenefitsAvailable;
